import { nextTick } from 'vue'

const STORAGE_KEY = 'kft:list-product-scroll-restore'

export interface ListProductScrollRestorePayload {
  path: string
  objectID: string
}

export function saveListProductScrollRestore(
  objectID: string,
  path: string
): void {
  if (!import.meta.client) return
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        objectID,
        path,
      } satisfies ListProductScrollRestorePayload)
    )
  } catch {
    // ignore quota / private mode
  }
}

function peekListProductScrollRestore(): ListProductScrollRestorePayload | null {
  if (!import.meta.client) return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ListProductScrollRestorePayload
  } catch {
    return null
  }
}

function clearListProductScrollRestore(): void {
  if (!import.meta.client) return
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export interface TryRestoreListProductScrollOptions {
  currentPath: string
  loading: boolean
  products: readonly { objectID: string }[]
  isProductsReady: boolean
  isRetry: boolean
}

export async function tryRestoreListProductScroll(
  options: TryRestoreListProductScrollOptions
): Promise<void> {
  const { currentPath, loading, products, isProductsReady, isRetry } = options
  if (!import.meta.client) return
  if (loading || !products.length || !isProductsReady) return

  const pending = peekListProductScrollRestore()
  if (!pending) return
  if (pending.path !== currentPath) {
    clearListProductScrollRestore()
    return
  }

  await nextTick()
  await nextTick()

  const el = document.querySelector(
    `[data-kft-list-product="${CSS.escape(pending.objectID)}"]`
  )
  if (el instanceof HTMLElement) {
    el.scrollIntoView({ block: 'center', behavior: 'auto' })
    clearListProductScrollRestore()
    return
  }

  const ids = new Set(products.map((p) => p.objectID))
  if (!ids.has(pending.objectID)) {
    clearListProductScrollRestore()
    return
  }

  if (!isRetry) {
    setTimeout(() => {
      void tryRestoreListProductScroll({
        ...options,
        isRetry: true,
      })
    }, 200)
  }
}
